import { Button, Typography } from "@mui/material";
import { forwardRef, useImperativeHandle } from "react";
import { StepProps, StepRef } from "../types";
import StaticStep from "../components/StaticStep";
import useIsMobile from "components/hooks/useIsMobile";
import {
  Baby,
  Briefcase,
  CircleDollarSign,
  Cross,
  Heart,
  ListTodo,
  PartyPopper,
  PawPrint,
  Plane,
  Wrench,
} from "lucide-react";
import FlexBox from "components/common/FlexBox";
import { FAQ } from "../components/FAQ";

const taskIcons = {
  mentalLoad: <ListTodo color="#198282" />,
  maintenance: <Wrench color="#198282" />,
  fun: <PartyPopper color="#198282" />,
  health: <Cross color="#198282" />,
  travel: <Plane color="#198282" />,
  money: <CircleDollarSign color="#198282" />,
  heart: <Heart color="#198282" />,
  kids: <Baby color="#198282" />,
  briefcase: <Briefcase color="#198282" />,
  pets: <PawPrint color="#198282" />,
};

export default forwardRef<StepRef, StepProps>(
  ({ onNext, signupState }: StepProps, ref) => {
    useImperativeHandle(ref, () => ({}));
    const isMobile = useIsMobile();

    // Goals
    const mentalLoad = !!signupState.goals?.["mental-load"];
    const saveMoney = !!signupState.goals?.["save-money"];
    const fun = !!signupState.goals?.["fun"];
    const maintenance = !!signupState.goals?.["maintenance"];
    const travel = !!signupState.goals?.["travel"];
    const health = !!signupState.goals?.["health"];

    // Household
    const hasPartner = !!signupState.household?.partner;
    const hasKids = !!signupState.household?.children;
    const hasDemandingJob = !!signupState.household?.["demanding-job"];
    const ownsBusiness = !!signupState.household?.["business-owner"];
    const homeowner = !!signupState.household?.homeowner;
    const hasPets = !!signupState.household?.pets;

    const exampleTasks: {
      icon: JSX.Element;
      title: string;
      conditions: boolean;
    }[] = [
      {
        icon: taskIcons["kids"],
        title: "Maintaining a calendar for keeping track of school activities",
        conditions: hasKids,
      },
      {
        icon: taskIcons["mentalLoad"],
        title: "Pre-scheduling grocery delivery or meal kits for busy weeks",
        conditions: hasDemandingJob,
      },
      {
        icon: taskIcons["heart"],
        title: "Making reservations to a new restaurant for date night",
        conditions: hasPartner,
      },
      {
        icon: taskIcons["briefcase"],
        title: "Making reservations for a team dinner",
        conditions: ownsBusiness && !hasPartner,
      },
      {
        icon: taskIcons["maintenance"],
        title: "Getting quotes to re-finish the floors",
        conditions: homeowner || maintenance,
      },
      {
        icon: taskIcons["pets"],
        title: "Researching the best senior dog joint supplements",
        conditions: hasPets,
      },
      {
        icon: taskIcons["kids"],
        title: "Finding reputable swim lessons",
        conditions: hasKids,
      },
      {
        icon: taskIcons["money"],
        title: "Auditing and negotiating monthly bills",
        conditions: saveMoney,
      },
      {
        icon: taskIcons["mentalLoad"],
        title: "Creating a weekly meal plan and ordering groceries",
        conditions: mentalLoad,
      },
      {
        icon: taskIcons["maintenance"],
        title: "Booking a handyman to wall-mount a large TV",
        conditions: maintenance,
      },
      {
        icon: taskIcons["fun"],
        title: "Researching new interesting local events to check out",
        conditions: fun,
      },
      {
        icon: taskIcons["travel"],
        title: "Creating a vacation packing list",
        conditions: travel,
      },
      {
        icon: taskIcons["heart"],
        title: "Keeping a running list of thoughtful surprise gifts",
        conditions: hasPartner,
      },
      {
        icon: taskIcons["health"],
        title: "Finding local in-network specialists or providers",
        conditions: health && !hasKids,
      },
      {
        icon: taskIcons["health"],
        title: "Finding in-network pediatricians",
        conditions: health && hasKids,
      },
      {
        icon: taskIcons["maintenance"],
        title: "Booking a mobile car detailer",
        conditions: maintenance,
      },
      {
        icon: taskIcons["mentalLoad"],
        title: "Scheduling recurring house cleaners or laundry pick-up",
        conditions: mentalLoad || hasDemandingJob,
      },
      {
        icon: taskIcons["briefcase"],
        title: "Sending flowers to a client",
        conditions: ownsBusiness,
      },
      {
        icon: taskIcons["pets"],
        title: "Scheduling annual vet check-ups and vaccinations",
        conditions: hasPets,
      },
      {
        icon: taskIcons["travel"],
        title: "Comparing and booking airport transport options",
        conditions: travel,
      },
      {
        icon: taskIcons["money"],
        title: "Researching and comparing rates for home/auto insurance",
        conditions: saveMoney,
      },
      {
        icon: taskIcons["mentalLoad"],
        title: "Keeping a running list of gift ideas",
        conditions: mentalLoad,
      },
      {
        icon: taskIcons["maintenance"],
        title: "Coordinating upholstery cleaning",
        conditions: maintenance,
      },
      {
        icon: taskIcons["fun"],
        title: "Planning a weekend getaway",
        conditions: fun || travel,
      },
      {
        icon: taskIcons["travel"],
        title: "Researching vacation restaurants and local spots",
        conditions: travel,
      },
      {
        icon: taskIcons["health"],
        title: "Tracking FSA-eligible expenses",
        conditions: health,
      },
      {
        icon: taskIcons["money"],
        title: "Tracking credit card perks and reimbursements",
        conditions: saveMoney,
      },
      {
        icon: taskIcons["pets"],
        title: "Finding a reputable pet sitter",
        conditions: hasPets,
      },
      {
        icon: taskIcons["mentalLoad"],
        title: "Cleaning out my email inbox",
        conditions: mentalLoad,
      },
    ];

    return (
      <StaticStep
        title="Based on your needs, your Advisor can help with tasks like:"
        img="/assets/images/nux/client-app-flatlay.png"
        mobileImg="/assets/images/nux/client-app-flatlay-mobile.png"
        bodyContent={
          <>
            <FlexBox direction="column" sx={{ marginTop: "12px" }}>
              {exampleTasks
                .filter((task) => task.conditions)
                .slice(0, isMobile ? 5 : 6)
                .map((task, idx) => (
                  <FlexBox
                    key={idx}
                    direction="row"
                    alignItems={isMobile ? "start" : "center"}
                  >
                    {task.icon}
                    <Typography
                      variant="body"
                      sx={{ flex: 1, justifyContent: "start" }}
                    >
                      {task.title}
                    </Typography>
                  </FlexBox>
                ))}
            </FlexBox>
            <FAQ
              question="What kinds of tasks aren’t a fit for Faye?"
              answer={
                <FlexBox direction="column">
                  <Typography variant="bodySmall">
                    Faye is best for virtual support. We don’t do hands-on tasks
                    like childcare, cleaning, or laundry—but if you're on an 8
                    or 16 hour plan and matched with a nearby Advisor, they can
                    run an errand in a pinch.
                  </Typography>
                  <Typography variant="bodySmall">
                    When something does need to happen in person, your Advisor
                    can help find the right people to get it done.
                  </Typography>
                </FlexBox>
              }
            />
          </>
        }
        actions={
          <Button
            variant="contained"
            color="primary"
            size={isMobile ? "medium" : "large"}
            // disabled={disabled}
            onClick={() => {
              onNext?.({ interruptTasks: true, interruptAboutAdvisors: true });
            }}
            sx={{
              width: isMobile ? "100%" : "auto",
            }}
          >
            Continue
          </Button>
        }
      />
    );
  },
);
